import React, { useEffect, useRef, useState } from "react";
import annotationPlugin from "chartjs-plugin-annotation";
import {
	Chart as ChartJS,
	LinearScale,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
} from "chart.js";
import { Bubble } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import { ChartJSOrUndefined } from "react-chartjs-2/dist/types";
import PropTypes from "prop-types";
import { numberFormatter } from "../../utils/formatter";
import ResetZoomButton from "../../components/ResetZoomButton";
import useGraphFontSize from "../../hooks/useGraphFontSize";

ChartJS.register(
	annotationPlugin,
	LinearScale,
	PointElement,
	LineElement,
	Tooltip,
	Legend,
	zoomPlugin
);

type Props = {
	data: any[];
	handleSummaryTabScatterGrphCall: (data: any) => void;
	selectedCustomerId: string;
	refX?: number;
	refY?: number;
	filterExpansion?: boolean;
};

const ScatterGraph: React.FC<Props> = ({
	data,
	handleSummaryTabScatterGrphCall,
	selectedCustomerId,
	refX,
	refY,
	filterExpansion = false,
}) => {
	const fontSize = useGraphFontSize();
	const chartRef = useRef<ChartJSOrUndefined<"bubble">>(null);
	const [plottedData, setPlottedData] = useState<any[]>([]);
	const [backgroundColor, setBackgroundColor] = useState<string[]>([]);
	const [borderColor, setBorderColor] = useState<string[]>([]);
	const [graphHeight, setGraphHeight] = useState<number>(250);
	const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
	useEffect(() => {
		const dataPoints: any = [];
		const backgroundColors: string[] = [];
		const borderColors: string[] = [];

		data.forEach((d: any, index: number) => {
			dataPoints.push({
				x: d.DM,
				y: d.GM,
				r: 7,
			});
			const color = getColor(d.color);
			backgroundColors.push(
				selectedCustomerId === d.id || index === selectedIndex
					? color
					: `${color}3D`
			);
			borderColors.push(
				selectedCustomerId === d.id || index === selectedIndex
					? color
					: `${color}3D`
			);
		});

		setGraphHeight(filterExpansion ? 250 : 215);
		setPlottedData(dataPoints);
		setBackgroundColor(backgroundColors);
		setBorderColor(borderColors);
	}, [data, selectedCustomerId, filterExpansion, selectedIndex]);

	const getColor = (val: string) => {
		switch (val?.toLowerCase()) {
			case "green":
				return "#86BC25";
			case "yellow":
				return "#EAA614";
			case "red":
				return "#DA5055";
			case "blue":
				return "#62B5E5";
			default:
				return "grey";
		}
	};

	const dataSet = {
		datasets: [
			{
				label: "Gross Margin vs Net Margin per Drop",
				data: plottedData,
				backgroundColor: backgroundColor,
				borderColor: borderColor,
			},
		],
	};

	const zoomOptions = {
		pan: {
			enabled: true,
			mode: "xy",
			modifierKey: "ctrl",
		},
		zoom: {
			mode: "xy",
			drag: {
				enabled: true,
				borderColor: "rgb(54, 162, 235)",
				borderWidth: 1,
				backgroundColor: "rgba(54, 162, 235, 0.3)",
			},
		},
	};

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		onClick: (e: any, elements: any) => {
			if (elements.length > 0) {
				const index = elements[0].index;
				setSelectedIndex(index);
				handleSummaryTabScatterGrphCall(data[index]);
			}
			else {
				setSelectedIndex(null)
				handleSummaryTabScatterGrphCall({ id: "" })
			}
		},
		pointRadius: 8,
		scales: {
			x: {
				border: {
					color: "black",
					width: 1,
				},
				type: "linear",
				position: "bottom",
				title: {
					display: true,
					text: "Net Margin per Drop ($/drop)",
					color: "black",
					font: {
						size: fontSize
					},
				},
				ticks: {
					display: true,
					callback: function (value: any) {
						return numberFormatter(value, 0) + "%";
					},
					maxTicksLimit: 5,
					color: "black",
					font: {
						size: fontSize
					},
				},

				grid: {
					display: false,
				},
			},
			y: {
				border: {
					color: "black",
					width: 1,
				},
				type: "linear",
				position: "left",
				title: {
					display: true,
					text: "Gross Margin (%)",
					color: "black",
					font: {
						size: fontSize
					},
				},
				ticks: {
					display: true,
					maxTicksLimit: 5,
					color: "black",
					callback: function (value: any) {
						return `${Number(value.toFixed(2))}%`;
					},
					font: {
						size: fontSize
					}
				},
				grid: {
					display: false,
				},
			},
		},
		plugins: {
			annotation: {
				annotations: {
					line1: {
						type: "line",
						yMin: refY !== undefined ? refY : 0,
						yMax: refY !== undefined ? refY : 0,
						borderDash: [4],
						borderColor: "#bebebe",
						borderWidth: [1],
						label: {
							content: `GM: ${refY !== undefined
								? numberFormatter(refY, 2)
								: "N/A"
								}%`,
							display: true,
							position: "end",
							yAdjust: -10,
							font: {
								size: fontSize,
								weight: 400,
							},
							backgroundColor: "transparent",
							borderWidth: 0,
							padding: 0,
							color: "black",
						},
					},
					line2: {
						type: "line",
						xMin: refX !== undefined ? refX : 0,
						xMax: refX !== undefined ? refX : 0,
						borderDash: [4],
						borderColor: "#bebebe",
						borderWidth: [1],
						label: {
							content: `DM: ${refX !== undefined
								? numberFormatter(refX, 2)
								: "N/A"
								}%`,
							display: true,
							position: "start",
							xAdjust: 40,
							font: {
								size: fontSize,
								weight: 400,
							},
							backgroundColor: "transparent",
							borderWidth: 0,
							padding: 0,
							color: "black",
						},
					},
				},
			},
			zoom: zoomOptions,
			tooltip: {
				callbacks: {
					label: function (context: any) {
						let element = data[context.dataIndex];
						return [`Customer: ${element.name}`,
						`Net Margin per Drop: ${numberFormatter(
							element.DM.toFixed(2)
						)}`,
						`Gross Margin: ${numberFormatter(
							element.GM.toFixed(2)
						)}%`,
						];
					},
					labelTextColor: function (context: any) {
						return "white";
					},
					labelFont: function (context: any) {
						return "10";
					},
				},
				displayColors: false,
				backgroundColor: "rgb(0, 0, 0, 0.7)",
				titleFont: {
					size: 12,
					color: "#000000",
				},
			},
			legend: {
				display: false,
			},
		},
	} as any;

	const handleResetZoom = () => {
		if (chartRef.current) {
			chartRef.current.resetZoom();
		}
	};

	return (
		<div
			className={`relative bg-white h-full w-full text-[fontSizepx] ${filterExpansion ? "" : "pt-4"
				}`}
		>
			<ResetZoomButton onClick={handleResetZoom} />{" "}
			{/* Use the common component */}
			<Bubble
				key={filterExpansion.toString()}
				ref={chartRef}
				options={options}
				data={dataSet}
				height={graphHeight}
			/>
		</div>
	);
};

export default ScatterGraph;


const CustomTooltip = ({ active, payload }: any) => {
	let payload1 = payload[0]?.payload;
	if (active && payload1) {
		return (
			<div className="custom-tooltip" style={{ backgroundColor: "#f1f1f1" }}>
				<p className="label" style={{ padding: "0", margin: "0" }}>
					<label style={{ color: "#000", fontSize: "10px", fontWeight: "normal" }}>Customer : </label>
					<label style={{ color: "#000", fontSize: "10px", fontWeight: "bold" }}>
						{payload1.name}
					</label>
				</p>
				<p className="label" style={{ padding: "0", margin: "0" }}>
					<label style={{ color: "#000", fontSize: "10px", fontWeight: "normal" }}>Delivered Margin ($/drop) : </label>
					<label style={{ color: "#000", fontSize: "10px", fontWeight: "bold" }}>
						{numberFormatter(payload1.DM) + "%"}
					</label>
				</p>
				<p className="label" style={{ padding: "0", margin: "0" }}>
					<label style={{ color: "#000", fontSize: "10px", fontWeight: "normal" }}>Gross Margin (%) : </label>
					<label style={{ color: "#000", fontSize: "10px", fontWeight: "bold" }}>
						{numberFormatter(payload1.GM) + `%`}
					</label>
				</p>
			</div>
		)
	}
	return <></>;
}